import { z } from 'zod';
import type { Timestamp } from 'firebase/firestore';

const currentYear = new Date().getFullYear();

const engineSchema = z.object({
  brand: z.string().nullable(),
  serialNumber: z.string().nullable(),
  powerHp: z.number().int().min(0).max(2000).nullable(),
});

const measurementCertSchema = z.object({
  exists: z.boolean(),
  issuedAt: z.union([z.date(), z.custom<Timestamp>()]).nullable(),
  fileRef: z.string().nullable(),
});

const selfDeclarationSchema = z.object({
  given: z.boolean(),
  basis: z.string().nullable(),
  fileRefs: z.array(z.string()),
  declaredAt: z.union([z.date(), z.custom<Timestamp>()]).nullable(),
});

export const boatSchema = z.object({
  name: z.string().nullable(),
  model: z.string().nullable(),
  hullSerialNumber: z.string().nullable(),
  modelYear: z.number().int().min(1900).max(currentYear).nullable(),
  modelYearUnknown: z.boolean(),
  engine: engineSchema.nullable(),
  insuranceCompany: z.string().nullable(),
  measurementCert: measurementCertSchema.nullable(),
  selfDeclaration: selfDeclarationSchema.nullable(),
});

export type BoatInput = z.infer<typeof boatSchema>;

const approvalSchema = z.object({
  approved: z.boolean(),
  approvedAt: z.union([z.date(), z.custom<Timestamp>()]).nullable(),
  approvedBy: z.string().nullable(),
  note: z.string().nullable(),
});

export interface Boat extends BoatInput {
  boatPlaceId: string;
  ownerUid: string;
  approval: z.infer<typeof approvalSchema> | null;
  updatedAt: Timestamp;
}

export const boatPlaceSchema = z.object({
  number: z.string().min(1),
  status: z.enum(['active', 'handed_over', 'removed']),
  statusChangedAt: z.union([z.date(), z.custom<Timestamp>()]).nullable(),
  statusNote: z.string().nullable(),
  currentOwnerUid: z.string().nullable(),
  notes: z.string().nullable(),
});

export type BoatPlaceInput = z.infer<typeof boatPlaceSchema>;

export interface BoatPlace extends BoatPlaceInput {
  id: string;
  updatedAt: Timestamp;
}

import { useEffect, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type Firestore,
} from 'firebase/firestore';
import { db } from '../firebase';

export interface UseBoatPlacesResult {
  places: BoatPlace[];
  loading: boolean;
  error: Error | null;
}

export function useMyBoatPlaces(uid: string | null): UseBoatPlacesResult {
  const [places, setPlaces] = useState<BoatPlace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!uid) {
      setPlaces([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = query(
      collection(db, 'boatPlaces'),
      where('currentOwnerUid', '==', uid),
    );
    return onSnapshot(
      q,
      (snap) => {
        setPlaces(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<BoatPlace, 'id'>) })),
        );
        setLoading(false);
        setError(null);
      },
      (e) => {
        setError(e);
        setLoading(false);
      },
    );
  }, [uid]);

  return { places, loading, error };
}

export interface UseBoatsResult {
  boats: (Boat & { id: string })[];
  loading: boolean;
  error: Error | null;
}

export function useMyBoats(uid: string | null): UseBoatsResult {
  const [boats, setBoats] = useState<(Boat & { id: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!uid) {
      setBoats([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = query(collection(db, 'boats'), where('ownerUid', '==', uid));
    return onSnapshot(
      q,
      (snap) => {
        setBoats(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as Boat) })),
        );
        setLoading(false);
        setError(null);
      },
      (e) => {
        setError(e);
        setLoading(false);
      },
    );
  }, [uid]);

  return { boats, loading, error };
}

export interface UseBoatResult {
  boat: (Boat & { id: string }) | null;
  loading: boolean;
  error: Error | null;
}

export function useBoat(boatId: string | null): UseBoatResult {
  const [boat, setBoat] = useState<(Boat & { id: string }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!boatId) {
      setBoat(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getDoc(doc(db, 'boats', boatId))
      .then((snap) => {
        if (cancelled) return;
        if (snap.exists()) {
          setBoat({ id: snap.id, ...(snap.data() as Boat) });
        } else {
          setBoat(null);
        }
        setLoading(false);
        setError(null);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e as Error);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [boatId]);

  return { boat, loading, error };
}

export async function createBoat(
  ownerUid: string,
  boatPlaceId: string,
  values: BoatInput,
): Promise<string> {
  const ref = await addDoc(collection(db, 'boats'), {
    ...values,
    boatPlaceId,
    ownerUid,
    approval: null,
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function saveBoat(boatId: string, values: BoatInput): Promise<void> {
  await setDoc(
    doc(db, 'boats', boatId),
    { ...values, updatedAt: serverTimestamp() },
    { merge: true },
  );
}

// ── Admin hooks ──────────────────────────────────────────────────────────────

export interface UseAllBoatPlacesResult {
  places: BoatPlace[];
  loading: boolean;
  error: Error | null;
}

export function useAllBoatPlaces(): UseAllBoatPlacesResult {
  const [places, setPlaces] = useState<BoatPlace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const q = query(collection(db, 'boatPlaces'), orderBy('number'));
    return onSnapshot(
      q,
      (snap) => {
        setPlaces(
          snap.docs.map((d) => ({
            id: d.id,
            ...(d.data() as Omit<BoatPlace, 'id'>),
          })),
        );
        setLoading(false);
        setError(null);
      },
      (e) => {
        setError(e);
        setLoading(false);
      },
    );
  }, []);

  return { places, loading, error };
}

export interface UseBoatPlaceResult {
  place: BoatPlace | null;
  loading: boolean;
  error: Error | null;
}

export function useBoatPlace(placeId: string | null): UseBoatPlaceResult {
  const [place, setPlace] = useState<BoatPlace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!placeId) {
      setPlace(null);
      setLoading(false);
      return;
    }
    const ref = doc(db, 'boatPlaces', placeId);
    return onSnapshot(
      ref,
      (snap) => {
        setPlace(
          snap.exists()
            ? ({ id: snap.id, ...(snap.data() as Omit<BoatPlace, 'id'>) })
            : null,
        );
        setLoading(false);
        setError(null);
      },
      (e) => {
        setError(e);
        setLoading(false);
      },
    );
  }, [placeId]);

  return { place, loading, error };
}

export function useBoatsForPlace(placeId: string | null): UseBoatsResult {
  const [boats, setBoats] = useState<(Boat & { id: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!placeId) {
      setBoats([]);
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, 'boats'),
      where('boatPlaceId', '==', placeId),
    );
    return onSnapshot(
      q,
      (snap) => {
        setBoats(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Boat) })));
        setLoading(false);
        setError(null);
      },
      (e) => {
        setError(e);
        setLoading(false);
      },
    );
  }, [placeId]);

  return { boats, loading, error };
}

export function useBoatsForOwner(uid: string | null): UseBoatsResult {
  return useMyBoats(uid);
}

// ── Admin helpers ────────────────────────────────────────────────────────────

export interface ApprovalInput {
  approved: boolean;
  note: string | null;
}

export async function setApproval(
  boatId: string,
  input: ApprovalInput,
  adminUid: string,
): Promise<void> {
  await updateDoc(doc(db, 'boats', boatId), {
    approval: {
      approved: input.approved,
      approvedAt: serverTimestamp(),
      approvedBy: adminUid,
      note: input.note,
    },
    updatedAt: serverTimestamp(),
  });
}

export async function clearApproval(boatId: string): Promise<void> {
  await updateDoc(doc(db, 'boats', boatId), {
    approval: null,
    updatedAt: serverTimestamp(),
  });
}

export async function assignPlaceOwner(
  placeId: string,
  ownerUid: string | null,
): Promise<void> {
  await updateDoc(doc(db, 'boatPlaces', placeId), {
    currentOwnerUid: ownerUid,
    updatedAt: serverTimestamp(),
  });
}

export async function setPlaceStatus(
  placeId: string,
  status: BoatPlaceInput['status'],
  statusNote: string | null,
): Promise<void> {
  await updateDoc(doc(db, 'boatPlaces', placeId), {
    status,
    statusNote,
    statusChangedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

async function fetchBoatsForPlace(
  database: Firestore,
  placeId: string,
): Promise<{ id: string; ref: ReturnType<typeof doc> }[]> {
  const q = query(
    collection(database, 'boats'),
    where('boatPlaceId', '==', placeId),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ref: d.ref }));
}

export async function transferBoatsToOwner(
  placeId: string,
  newOwnerUid: string,
): Promise<void> {
  const boats = await fetchBoatsForPlace(db, placeId);
  const batch = writeBatch(db);
  for (const b of boats) {
    batch.update(b.ref, {
      ownerUid: newOwnerUid,
      updatedAt: serverTimestamp(),
    });
  }
  await batch.commit();
}

export async function detachBoatsFromPlace(placeId: string): Promise<void> {
  const boats = await fetchBoatsForPlace(db, placeId);
  const batch = writeBatch(db);
  for (const b of boats) {
    batch.update(b.ref, {
      boatPlaceId: null,
      updatedAt: serverTimestamp(),
    });
  }
  await batch.commit();
}

export async function deleteBoatsForPlace(placeId: string): Promise<void> {
  const boats = await fetchBoatsForPlace(db, placeId);
  const batch = writeBatch(db);
  for (const b of boats) {
    batch.delete(b.ref);
  }
  await batch.commit();
}

export async function createBoatPlace(number: string): Promise<void> {
  await setDoc(doc(db, 'boatPlaces', number), {
    number,
    status: 'active',
    statusChangedAt: null,
    statusNote: null,
    currentOwnerUid: null,
    notes: null,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteBoatPlace(placeId: string): Promise<void> {
  await deleteDoc(doc(db, 'boatPlaces', placeId));
}
